import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Trophy, Users, Swords, Shield, Calendar,
  ExternalLink, ChevronRight, Star,
  Medal, Crown, Zap, Award
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || '/api';

function formatDate(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  } catch { return dateStr; }
}

function statusLabel(s) {
  return {
    registration: 'Inscriptions',
    seeding: 'Seeding',
    'in_progress': 'En cours',
    'in-progress': 'En cours',
    finished: 'Terminé'
  }[s] || s;
}

/* ═══════════════════════════════════════════════
   PAGE PRINCIPALE
═══════════════════════════════════════════════ */
export default function HomePage() {
  const navigate = useNavigate();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/tournaments`)
      .then(r => r.json())
      .then(d => { setTournaments(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="home-page lp2">
      <HeroSection navigate={navigate} tournaments={tournaments} />
      <TournamentsSection tournaments={tournaments} loading={loading} />
      <LeaderboardSection />
      <FeaturesSection navigate={navigate} />
      <ChampionsSection />
      <NewsSection />
    </motion.div>
  );
}

/* ═══════════════════════════════════════════════
   Hook — effet tilt sur un élément
═══════════════════════════════════════════════ */
function useTilt() {
  const ref = useRef(null);

  const onMouseMove = useCallback((e) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width - 0.5;
    const y = (e.clientY - rect.top)  / rect.height - 0.5;
    el.style.transition = 'transform 0.08s ease';
    el.style.transform = `perspective(900px) rotateY(${x * 7}deg) rotateX(${-y * 5}deg) translateZ(6px)`;
  }, []);

  const onMouseLeave = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.transition = 'transform 0.45s ease';
    el.style.transform = 'perspective(900px) rotateY(0deg) rotateX(0deg) translateZ(0px)';
  }, []);

  return { ref, onMouseMove, onMouseLeave };
}

/* ═══════════════════════════════════════════════
   HERO — split layout
═══════════════════════════════════════════════ */
function HeroSection({ navigate, tournaments }) {
  const recent = [...tournaments]
    .sort((a, b) => new Date(b.startDate || 0) - new Date(a.startDate || 0))
    .slice(0, 4);
  const totalTeams = tournaments.reduce((s, t) => s + (t.teams?.length || 0), 0);
  const tilt = useTilt();

  return (
    <section className="lp2-hero">
      <div className="lp2-hero-grid-bg" />
      <div className="lp2-hero-glow lp2-hero-glow--1" />
      <div className="lp2-hero-glow lp2-hero-glow--2" />

      {/* Gauche : texte */}
      <motion.div
        className="lp2-hero-left"
        initial={{ opacity: 0, x: -30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.55 }}
      >
        <div className="lp2-hero-tag">
          <span className="lp2-hero-tag__dot" />
          NO TRY HARD, NO GAIN
        </div>

        <h1 className="lp2-hero-title">
          <span className="lp2-hero-title__white">LOL&nbsp;</span>
          <span className="lp2-hero-title__grad">ARENA</span>
        </h1>

        <p className="lp2-hero-sub">
          La plateforme de tournois League of Legends.<br />
          Créez votre équipe et affrontez les meilleurs.
        </p>

        <button className="lp2-hero-btn" onClick={() => navigate('/riot-login')}>
          <Shield size={18} />
          Rejoindre l'arène
        </button>

        <div className="lp2-hero-stat">
          <div className="lp2-hero-stat__avatars">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className={`lp2-hero-stat__av lp2-hero-stat__av--${i}`} />
            ))}
          </div>
          <div className="lp2-hero-stat__text">
            <strong>{totalTeams > 0 ? `${totalTeams}+` : '—'}</strong>
            <span>Équipes actives</span>
          </div>
        </div>
      </motion.div>

      {/* Personnage — absolu sur la section, motion.div pour l'anim, img pour le CSS transform */}
      <motion.div
        className="lp2-hero-char-wrap"
        initial={{ opacity: 0, y: 20, x: "-50%" }}
        animate={{ opacity: 1, y: 0, x: "-50%" }}
        transition={{ duration: 0.65, delay: 0.25 }}
      >
        <img
          src="/warwock_trans.png"
          alt=""
          aria-hidden="true"
          className="lp2-hero-character"
        />
      </motion.div>

      {/* Droite : panneau derniers tournois */}
      <motion.div
        className="lp2-hero-right"
        initial={{ opacity: 0, x: 30 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.55, delay: 0.15 }}
      >
        <div className="lp2-winners-panel" ref={tilt.ref} onMouseMove={tilt.onMouseMove} onMouseLeave={tilt.onMouseLeave}>
          <div className="lp2-winners-panel__head">
            <Trophy size={14} />
            Derniers Tournois
          </div>

          {recent.length === 0 ? (
            <div className="lp2-winners-panel__empty">
              <Swords size={28} />
              <p>Aucun tournoi disponible</p>
            </div>
          ) : (
            <div className="lp2-winners-panel__list">
              {recent.map((t, i) => (
                <Link key={t._id || i} to={`/tournament/${t._id}`} className="lp2-winners-item">
                  <div className="lp2-winners-item__num">
                    {i === 0 ? <Crown size={13} /> : `#${i + 1}`}
                  </div>
                  <div className="lp2-winners-item__info">
                    <span className="lp2-winners-item__name">{t.name}</span>
                    <span className="lp2-winners-item__meta">
                      {t.teams?.length || 0}/{t.maxTeams || 16} équipes
                      {t.startDate && ` · ${formatDate(t.startDate)}`}
                    </span>
                  </div>
                  <span className={`lp2-status-dot lp2-status-dot--${(t.status || '').replace(/_/g, '-')}`}>
                    {statusLabel(t.status)}
                  </span>
                </Link>
              ))}
            </div>
          )}

          <Link to="/tournaments" className="lp2-winners-panel__footer">
            Tous les tournois <ChevronRight size={13} />
          </Link>
        </div>

        <div className="lp2-hero-medal">
          <Medal size={26} />
        </div>
      </motion.div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   TOURNOIS — grille 3 colonnes
═══════════════════════════════════════════════ */
const SPLASH_ART = [
  'https://ddragon.leagueoflegends.com/cdn/img/champion/splash/Jinx_0.jpg',
  'https://ddragon.leagueoflegends.com/cdn/img/champion/splash/Yasuo_0.jpg',
  'https://ddragon.leagueoflegends.com/cdn/img/champion/splash/Ahri_0.jpg',
  'https://ddragon.leagueoflegends.com/cdn/img/champion/splash/Lux_0.jpg',
  'https://ddragon.leagueoflegends.com/cdn/img/champion/splash/Ezreal_0.jpg',
  'https://ddragon.leagueoflegends.com/cdn/img/champion/splash/Thresh_0.jpg',
];

function TournamentsSection({ tournaments, loading }) {
  const shown = tournaments.filter(t => t.status !== 'finished').slice(0, 8);
  const isEmpty = !loading && shown.length === 0;
  // Duplication pour boucle infinie seamless (le track se déplace de -50%)
  const loopItems = shown.length > 0 ? [...shown, ...shown] : [];

  return (
    <section className="lp2-section">
      <div className="lp2-container">
        <div className="lp2-section-head">
          <h2 className="lp2-section-h2">Tournois</h2>
          <Link to="/tournaments" className="lp2-view-more-btn">
            Voir plus <ChevronRight size={14} />
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="lp2-tour-carousel">
          <div className="lp2-tour-track" style={{ animation: 'none' }}>
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} className="lp2-skeleton" style={{ height: 340, borderRadius: 16 }} />
            ))}
          </div>
        </div>
      ) : isEmpty ? (
        <div className="lp2-container">
          <div className="lp2-empty-state">
            <Calendar size={40} />
            <p>Aucun tournoi disponible pour le moment</p>
            <Link to="/tournaments" className="lp2-view-more-btn" style={{ marginTop: '0.5rem' }}>
              Aller à la page tournois
            </Link>
          </div>
        </div>
      ) : (
        <div className="lp2-tour-carousel">
          <div className="lp2-tour-track">
            {loopItems.map((t, i) => (
              <TourCard key={`${t._id || i}-${i}`} t={t} i={i % shown.length} />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function TourCard({ t, i }) {
  const teamsCount = t.teams?.length || 0;
  const maxTeams = t.maxTeams || 16;
  const isPlaying = t.status === 'in_progress' || t.status === 'in-progress';
  const bgUrl = t.logo || t.image || SPLASH_ART[i % SPLASH_ART.length];

  return (
    <div className="lp2-tour-card">
      <Link to={`/tournament/${t._id}`} className="lp2-tour-card__link">
        <div className="lp2-tour-card__img">
          <img src={bgUrl} alt={t.name} loading="lazy"
            onError={e => { e.target.style.display = 'none'; }} />
          <div className="lp2-tour-card__img-overlay" />
          <div className={`lp2-tour-badge ${isPlaying ? 'lp2-tour-badge--playing' : 'lp2-tour-badge--open'}`}>
            <span className="lp2-tour-badge__dot" />
            {statusLabel(t.status)}
          </div>
        </div>
        <div className="lp2-tour-card__body">
          <h3 className="lp2-tour-card__name">{t.name}</h3>
          <p className="lp2-tour-card__sub">{t.region || 'Toutes régions'}</p>
          <div className="lp2-tour-card__row">
            <span><Users size={12} /> Entrée gratuite</span>
            {t.startDate && <span><Calendar size={12} />{formatDate(t.startDate)}</span>}
          </div>
          <div className="lp2-tour-card__footer">
            <span><Users size={12} />{teamsCount}/{maxTeams} équipes</span>
            <div className="lp2-tour-card__btn">
              <ChevronRight size={15} />
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
}

/* ═══════════════════════════════════════════════
   LEADERBOARD — Classement global
═══════════════════════════════════════════════ */
function LeaderboardSection() {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/public/leaderboard`)
      .then(r => r.json())
      .then(d => { setTeams(d.teams || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return null;

  const rankIcon = (i) => {
    if (i === 0) return <Crown size={18} className="lp2-lb-icon lp2-lb-icon--gold" />;
    if (i === 1) return <Medal size={18} className="lp2-lb-icon lp2-lb-icon--silver" />;
    if (i === 2) return <Medal size={18} className="lp2-lb-icon lp2-lb-icon--bronze" />;
    return <span className="lp2-lb-rank">#{i + 1}</span>;
  };

  return (
    <section className="lp2-section lp2-section--dark">
      <div className="lp2-container">
        <div className="lp2-section-header">
          <h2 className="lp2-section-title"><Trophy size={22} /> Classement Global</h2>
          <Link to="/teams" className="lp2-section-link">Voir tout <ChevronRight size={15} /></Link>
        </div>

        <div className="lp2-lb-table">
          <div className="lp2-lb-header">
            <span className="lp2-lb-col lp2-lb-col--rank">#</span>
            <span className="lp2-lb-col lp2-lb-col--team">Équipe</span>
            <span className="lp2-lb-col lp2-lb-col--stats">V / D</span>
            <span className="lp2-lb-col lp2-lb-col--pts">Points</span>
          </div>
          {teams.length === 0 && (
            <div className="lp2-lb-empty">Aucune équipe classée pour le moment.</div>
          )}
          {teams.slice(0, 10).map((t, i) => (
            <motion.div
              key={t.id}
              className={`lp2-lb-row${i < 3 ? ' lp2-lb-row--top' : ''}`}
              initial={{ opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.04 }}
            >
              <span className="lp2-lb-col lp2-lb-col--rank">{rankIcon(i)}</span>
              <span className="lp2-lb-col lp2-lb-col--team">
                <Link to={`/team/${t.id}`} className="lp2-lb-team-link">
                  {t.logo && <img src={t.logo} alt="" className="lp2-lb-logo" />}
                  <span className="lp2-lb-name">{t.name}</span>
                  {t.tag && <span className="lp2-lb-tag">[{t.tag}]</span>}
                </Link>
              </span>
              <span className="lp2-lb-col lp2-lb-col--stats">{t.wins || 0}V / {t.losses || 0}D</span>
              <span className="lp2-lb-col lp2-lb-col--pts">{t.points || 0}</span>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   FEATURES — "Montez votre jeu"
═══════════════════════════════════════════════ */
const FEATURES = [
  { icon: <Star size={22} />,    title: 'Support communautaire',  desc: 'Une équipe disponible pour répondre à vos questions et organiser vos tournois.' },
  { icon: <Swords size={22} />,  title: 'Tournois compétitifs',   desc: 'Des formats variés, des brackets automatiques, des finales épiques.' },
  { icon: <Users size={22} />,   title: 'Gestion d\'équipes',     desc: 'Invitez vos coéquipiers, gérez votre roster, participez ensemble.' },
  { icon: <Shield size={22} />,  title: 'Inscriptions gratuites', desc: 'Toutes les compétitions sont accessibles sans frais d\'inscription.' },
  { icon: <Zap size={22} />,     title: 'Codes automatiques',     desc: 'Les codes de parties sont envoyés automatiquement aux capitaines.' },
  { icon: <Award size={22} />,   title: 'Classements en direct',  desc: 'Vos statistiques et classements mis à jour en temps réel.' },
];

function FeaturesSection({ navigate }) {
  return (
    <section className="lp2-section lp2-section--dark2">
      <div className="lp2-container">
        <div className="lp2-feat-layout">
          <motion.div
            className="lp2-feat-heading-col"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="lp2-feat-heading">
              Montez<br />votre jeu<br />au prochain<br />niveau
            </h2>
          </motion.div>

          <div className="lp2-feat-grid">
            {FEATURES.map((f, i) => (
              <motion.div
                key={i}
                className={`lp2-feat-card${i === 5 ? ' lp2-feat-card--accent' : ''}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.07 }}
              >
                <div className="lp2-feat-icon">{f.icon}</div>
                <h3 className="lp2-feat-title">{f.title}</h3>
                <p className="lp2-feat-desc">{f.desc}</p>
                <button className="lp2-feat-link" onClick={() => navigate('/riot-login')}>
                  Commencer →
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   CHAMPIONS — rotation / populaires
═══════════════════════════════════════════════ */
function ChampionsSection() {
  const [champions, setChampions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isReal, setIsReal] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/champion-rotation`)
      .then(r => r.json())
      .then(data => {
        if (data.champions?.length > 0) {
          setChampions(data.champions);
          setIsReal(data.isRealRotation);
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (!loading && champions.length === 0) return null;

  return (
    <section className="lp2-section">
      <div className="lp2-container">
        <div className="lp2-section-head">
          <h2 className="lp2-section-h2">
            {isReal ? 'Rotation gratuite' : 'Champions populaires'}
          </h2>
        </div>
        {loading ? (
          <div className="lp2-champ-grid">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="lp2-skeleton" style={{ height: 110, borderRadius: 12 }} />
            ))}
          </div>
        ) : (
          <div className="lp2-champ-grid">
            {champions.map((c, i) => (
              <motion.div
                key={c.id}
                className="lp2-champ-card"
                initial={{ opacity: 0, scale: 0.88 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.025 }}
                whileHover={{ scale: 1.08, zIndex: 2 }}
              >
                <img src={c.squareUrl} alt={c.name} loading="lazy"
                  onError={e => { e.target.src = c.imageUrl; }} />
                <span>{c.name}</span>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

/* ═══════════════════════════════════════════════
   NEWS — actualités LoL
═══════════════════════════════════════════════ */
function NewsSection() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API}/public/lol-news`)
      .then(r => r.json())
      .then(data => {
        if (data.articles?.length > 0) setNews(data.articles);
        else setError(true);
        setLoading(false);
      })
      .catch(() => { setError(true); setLoading(false); });
  }, []);

  if (!loading && error) return null;

  return (
    <section className="lp2-section lp2-section--dark2">
      <div className="lp2-container">
        <div className="lp2-section-head">
          <h2 className="lp2-section-h2">Actualités LoL</h2>
          <a href="https://www.leagueoflegends.com/fr-fr/news/" target="_blank"
            rel="noopener noreferrer" className="lp2-view-more-btn">
            Voir tout <ChevronRight size={14} />
          </a>
        </div>
        {loading ? (
          <div className="lp2-news-grid">
            {[0, 1, 2, 3, 4, 5].map(i => (
              <div key={i} className="lp2-skeleton" style={{ height: 220, borderRadius: 14 }} />
            ))}
          </div>
        ) : (
          <div className="lp2-news-grid">
            {news.slice(0, 6).map((article, i) => (
              <motion.a
                key={i}
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="lp2-news-card"
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <div className="lp2-news-card__img">
                  {article.imageUrl
                    ? <img src={article.imageUrl} alt={article.title} loading="lazy" />
                    : <div className="lp2-news-card__img-fb"><ExternalLink size={22} /></div>
                  }
                  {article.category && (
                    <span className="lp2-news-card__cat">{article.category}</span>
                  )}
                </div>
                <div className="lp2-news-card__body">
                  <h3>{article.title}</h3>
                  {article.date && <span className="lp2-news-card__date">{formatDate(article.date)}</span>}
                </div>
              </motion.a>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
